<template>
    <v-container fluid>
      <v-card elevation="2">
        <v-card-title class="d-flex align-center pe-2 tableheader white--text">
          <v-icon left color="white">mdi-home-account</v-icon>
          Occupancy Records
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            density="compact"
            label="Search (Tenant, Unit, Dates)"
            prepend-inner-icon="mdi-magnify"
            variant="solo-filled"
            flat
            hide-details
            single-line
            class="me-2 search-field"
          ></v-text-field>
          <v-btn color="primary" @click="openAddDialog" :loading="dialogLoading" v-if="authStore.canEdit">
            <v-icon left>mdi-plus</v-icon> Add Record
          </v-btn>
          
          <!-- <ExportButtons entity="occupancies" filename="Occupancies" :exclude="['id','created_at','updated_at', 'lease_doc', 'deposit_status', 'utilities_included', 'deposit_amount', 'notice_period_days']"></ExportButtons> -->
          <ExportButtons entity="occupancies" filename="Occupancies" :headers="headers"></ExportButtons>
        </v-card-title>
  
        <v-divider></v-divider>
  
        <v-data-table
          :headers="headers"
          :items="occupancies"
          :search="search"
          item-value="id"
          class="elevation-1"
          :loading="tableLoading"
          loading-text="Loading occupancy records... Please wait"
          hover
        >
          <template v-slot:[`item.tenant_name`]="{ item }">
            {{ item.tenant_first_name }} {{ item.tenant_last_name }}
          </template>
          <template v-slot:[`item.start_date`]="{ value }">
            {{ formatDateForDisplay(value) }}
          </template>
          <template v-slot:[`item.end_date`]="{ value }">
            {{ formatDateForDisplay(value) }}
          </template>
          <template v-slot:[`item.rate`]="{ value }">
              {{ formatCurrency(value) }}
          </template>
          
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip location="top" text="View Details">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
                mdi-eye-outline
              </v-icon>
            </template>
          </v-tooltip>

          <v-tooltip location="top" text="Edit Occupancy">  
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="editItem(item)" color="secondary" v-if="authStore.canEdit">mdi-pencil</v-icon>
            </template>
          </v-tooltip>
          
          <v-tooltip location="top" text="Delete Occupancy">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="deleteItemPrompt(item)" color="error" v-if="authStore.isAdmin">mdi-delete</v-icon>
            </template>
          </v-tooltip>
          </template>
          <template v-slot:[`no-data`]>
            <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
              {{ tableLoading ? 'Fetching data...' : 'No occupancy records available or match your search.' }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="dialog" persistent max-width="700px" @update:modelValue="val => !val && closeDialog()">
        <v-card :loading="dialogLoading">
          <v-card-title class="tableheader white--text">
            <span class="text-h5">{{ formTitle }}</span>
          </v-card-title>
          <v-form ref="occupancyForm" v-model="isFormValid" @submit.prevent="saveItem">
            <v-card-text class="mt-4">
              <v-container>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editedItem.tenant_id"
                      :items="availableTenants"
                      item-title="fullName"
                      item-value="id"
                      label="Tenant*"
                      variant="outlined"
                      :rules="[rules.required]"
                      required
                      :loading="loadingTenants"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editedItem.unit_id"
                      :items="availableUnits"
                      item-title="name"
                      item-value="id"
                      label="Unit*"
                      variant="outlined"
                      :rules="[rules.required]"
                      required
                      :loading="loadingUnits"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                     <v-text-field
                      v-model="editedItem.start_date"
                      label="Start Date*"
                      type="date"
                      variant="outlined"
                      :rules="[rules.required, rules.validDate]"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                     <v-text-field
                      v-model="editedItem.end_date"
                      label="End Date (Optional)"
                      type="date"
                      variant="outlined"
                      clearable
                      :rules="[rules.validDateOptional, rules.endDateAfterStart]"
                    ></v-text-field>
                  </v-col>
                  <!-- <v-col cols="12" md="6">
                    <v-select
                      v-model="editedItem.occupancy_type"
                      :items="occupancyTypeOptions"
                      item-title="text"
                      item-value="value"
                      label="Occupancy Type*"
                      variant="outlined"
                      :rules="[rules.required]"
                      required
                    ></v-select>
                  </v-col> -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.number="editedItem.rate"
                      label="Rate (per type)*"
                      type="number"
                      :prefix="getCurrencyPrefix()"
                      variant="outlined"
                      :rules="[rules.required, rules.positiveNumber]"
                      required
                    ></v-text-field>
                  </v-col>

                  <!-- new fields -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.number="editedItem.deposit_amount"
                      label="Deposit Amount"
                      type="number"
                      :prefix="getCurrencyPrefix()"
                      variant="outlined"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editedItem.deposit_status"
                      :items="depositStatuses"
                      item-title="text"
                      item-value="value"
                      label="Deposit Status"
                      variant="outlined"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editedItem.rent_cycle"
                      :items="rentCycles"
                      item-title="text"
                      item-value="value"
                      label="Rent Cycle"
                      variant="outlined"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-checkbox
                      v-model="editedItem.utilities_included"
                      label="Utilities Included"
                      variant="outlined"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editedItem.occupancy_status"
                      :items="occupancyStates"
                      item-title="text"
                      item-value="value"
                      label="Occupancy Status"
                      variant="outlined"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                    v-model.number="editedItem.notice_period_days"
                      type="number"
                      label="Notice Period Days"
                      variant="outlined"
                      :rules="[rules.positiveNumberOptional]"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-checkbox
                      v-model="editedItem.is_active"
                      label="Is Active"
                      variant="outlined"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="editedItem.lease_doc"
                      label="Lease Document"
                      variant="outlined"
                    ></v-file-input>
                  </v-col>
                  <!-- end new fields -->
                </v-row>
              </v-container>
              <small class="pl-3">*indicates required field</small>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey-darken-1" variant="text" @click="closeDialog" :disabled="dialogLoading">Cancel</v-btn>
              <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
  
      <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
      <v-card :loading="viewDialogLoading">
        <v-card-title class="tableheader white--text d-flex align-center">
          <v-icon left color="white" class="mr-2">mdi-home-account</v-icon>
          <span class="text-h5">Occupant Details: {{ viewItem?.tenant_first_name + ' ' + viewItem?.tenant_last_name }}</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="viewItem" class="pt-4">
          <v-list lines="two" density="compact">
            <!-- <v-list-item title="ID" :subtitle="viewItem.id"></v-list-item><v-divider inset></v-divider> -->
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Tenant:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.tenant_first_name + ' ' + viewItem.tenant_last_name }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Unit:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.unit_name }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Start Date:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.start_date) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">End Date:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.end_date) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Occupancy Type:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatOccupancyType(viewItem.occupancy_type) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->

            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Rate:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatCurrency(viewItem.rate) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Deposit Amount:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatCurrency(viewItem.deposit_amount) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Deposit Status:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.deposit_status }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Rent Cycle:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.rent_cycle }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Utilities Included:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.utilities_included ? 'Yes' : 'No' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Occupancy Status:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.occupancy_status }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Notice Period Days:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.notice_period_days }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Is Active:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.is_active ? 'Yes' : 'No' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Lease Document:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.lease_doc }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->
          </v-list>
        </v-card-text>
        <v-card-text v-else class="text-center py-5">
            <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
            <p>Loading unit details...</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card :loading="dialogLoading">
          <v-card-title class="text-h5">Confirm Deletion</v-card-title>
          <v-card-text>
            Are you sure you want to delete this occupancy record for
            {{ itemToDelete?.tenant_first_name }} {{ itemToDelete?.tenant_last_name }} in unit {{ itemToDelete?.unit_name }}?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="red-darken-1" text @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
        {{ snackbar.message }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, nextTick } from 'vue';
  import OccupancyService from '@/services/OccupancyService';
  import TenantService from '@/services/TenantService'; // For tenant dropdown
  import UnitService from '@/services/UnitService';   // For unit dropdown
  import { useAuthStore } from '@/stores/auth';
  import { formatDateForDisplay, formatCurrency, getCurrencyPrefix } from '@/services/genFunctions';
  import ExportButtons from '@/components/ExportButtons.vue';

  const authStore = useAuthStore();
  const search = ref('');
  const tableLoading = ref(true);
  const dialog = ref(false);
  const dialogLoading = ref(false);
  const deleteDialog = ref(false);
  const occupancyForm = ref(null);
  const isFormValid = ref(false);
  
  const snackbar = ref({ show: false, message: '', color: 'success', timeout: 3000 });
  const showSnackbar = (message, color = 'success', timeout = 3000) => {
    snackbar.value = { message, color, timeout, show: true };
  };
  
  const defaultItem = {
    id: null,
    tenant_id: null,
    unit_id: null,
    start_date: null, // Will be YYYY-MM-DD string
    end_date: null,   // Will be YYYY-MM-DD string or null
    // occupancy_type: null,
    rate: null,
    deposit_amount: null,
    deposit_status: null,
    rent_cycle: null,
    utilities_included: false,
    occupancy_status: null,
    notice_period_days: null,
    is_active: true,
    lease_doc: null,
  };
  const editedItem = ref({ ...defaultItem });
  const itemToDelete = ref(null);
  
  const availableTenants = ref([]);
  const loadingTenants = ref(false);
  const availableUnits = ref([]);
  const loadingUnits = ref(false);
  
  const rentCycles      = [
    { text: 'Monthly', value: 'monthly' },
    { text: 'Weekly', value: 'weekly' },
    { text: 'Daily', value: 'daily' }
  ];
  const depositStatuses = [
    { text: 'Held', value: 'held' },
    { text: 'Returned', value: 'returned' },
    { text: 'Forfeited', value: 'forfeited' }
  ];
  const occupancyStates = [
    { text: 'Active', value: 'active' },
    { text: 'Terminated', value: 'terminated' },
    { text: 'Renewed', value: 'renewed' }
  ];

  const viewDialog = ref(false);
  const viewDialogLoading = ref(false);
  const viewItem = ref(null);

  
  const rules = {
    required: value => !!value || 'This field is required.',
    positiveNumber: value => (typeof value === 'number' && value > 0) || 'Must be a positive number > 0.',
    validDate: value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') || 'Invalid date format (YYYY-MM-DD).',
    validDateOptional: value => (!value || /^\d{4}-\d{2}-\d{2}$/.test(value)) || 'Invalid date format (YYYY-MM-DD).',
    endDateAfterStart: value => {
      if (!value || !editedItem.value.start_date) return true; // If no end date or no start date, valid
      return new Date(value) >= new Date(editedItem.value.start_date) || 'End date must be after start date.';
    },
    positiveNumberOptional: value => {
      if (!value) return true;
      return (typeof value === 'number' && value >= 0) || 'Must be a positive number >= 0.';
    }
  };
  
  const headers = ref([
    // { title: 'ID', key: 'id', align: 'start', sortable: true, width: '70px' },
    { title: 'Tenant', key: 'tenant_name', value: item => `${item.tenant_first_name} ${item.tenant_last_name}`, sortable: true },
    { title: 'Unit', key: 'unit_name', sortable: true },
    { title: 'Start Date', key: 'start_date', sortable: true },
    { title: 'End Date', key: 'end_date', sortable: true },
    // { title: 'Type', key: 'occupancy_type', sortable: true },
    { title: 'Rate', key: 'rate', align: 'end', sortable: true },
        // { title: 'Deposit Amount', key: 'deposit_amount', align: 'end', sortable: true },
    // { title: 'Deposit Status', key: 'deposit_status', sortable: true },
    { title: 'Rent Cycle', key: 'rent_cycle', sortable: true },
    // { title: 'Utilities Included', key: 'utilities_included', sortable: true },
    { title: 'Occupancy Status', key: 'occupancy_status', sortable: true },
    // { title: 'Is Active', key: 'is_active', sortable: true },
    // { title: 'Notice Period Days', key: 'notice_period_days', sortable: true },

    { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '130px' },
  ]);
  
  const occupancies = ref([]);
  
  const fetchOccupancies = async () => {
    tableLoading.value = true;
    try {
      occupancies.value = await OccupancyService.getOccupancies();
    } catch (error) {
      console.error("Error fetching occupancies:", error);
      showSnackbar('Failed to load occupancy records.', 'error');
      occupancies.value = [];
    } finally {
      tableLoading.value = false;
    }
  };
  
  const fetchFormData = async () => {
    loadingTenants.value = true;
    loadingUnits.value = true;
    try {
      const tenantsData = await TenantService.getTenants();
      availableTenants.value = tenantsData.map(t => ({ ...t, fullName: `${t.first_name} ${t.last_name}` }));
  
      const unitsData = await UnitService.getUnits(); // Assuming you have this from before
      availableUnits.value = unitsData;
    } catch (error) {
      console.error("Error fetching form data for occupancy:", error);
      showSnackbar('Failed to load tenant/unit data for form.', 'error');
    } finally {
      loadingTenants.value = false;
      loadingUnits.value = false;
    }
  };
  
  onMounted(() => {
    fetchOccupancies();
    fetchFormData(); // Fetch data for dropdowns
  });
  
  // const formatOccupancyType = (value) => {
  //     const option = occupancyTypeOptions.value.find(opt => opt.value === value);
  //     return option ? option.text : (value || 'N/A');
  // };
  
  const formTitle = computed(() => !editedItem.value.id ? 'New Occupancy Record' : 'Edit Occupancy Record');
  
  const openAddDialog = () => {
    editedItem.value = { ...defaultItem, start_date: new Date().toISOString().split('T')[0] }; // Default start_date to today
    dialog.value = true;
    nextTick(() => occupancyForm.value?.resetValidation());
  };
  
  const editItem = async (item) => {
    dialogLoading.value = true;
    try {
      const fullOccupancyData = await OccupancyService.getOccupancy(item.id);
      editedItem.value = { ...fullOccupancyData };

      dialog.value = true;
      nextTick(() => occupancyForm.value?.resetValidation());
    } catch (error) {
      showSnackbar('Failed to load occupancy details for editing.', 'error');
      console.error("Error fetching occupancy for edit:", error);
    } finally {
      dialogLoading.value = false;
    }
  };
  
  const deleteItemPrompt = (item) => {
    itemToDelete.value = { ...item };
    deleteDialog.value = true;
  };
  
  const closeDialog = async () => {
    dialog.value = false;
    await nextTick();
    editedItem.value = { ...defaultItem };
    occupancyForm.value?.resetValidation();
    dialogLoading.value = false;
  };
  
  const closeDeleteDialog = () => {
    deleteDialog.value = false;
    nextTick(() => itemToDelete.value = null);
    dialogLoading.value = false;
  };
  
  const saveItem = async () => {
    if (!occupancyForm.value) return;
    const { valid } = await occupancyForm.value.validate();
    if (!valid) {
      showSnackbar('Please correct the form errors.', 'warning');
      return;
    }
    dialogLoading.value = true;
    try {
      if (editedItem.value.id) {
        const updatedRecord = await OccupancyService.updateOccupancy(editedItem.value.id, editedItem.value);
        const index = occupancies.value.findIndex(o => o.id === updatedRecord.id);
        if (index > -1) occupancies.value.splice(index, 1, updatedRecord);
        else await fetchOccupancies(); // Fallback
        showSnackbar('Occupancy record updated successfully!', 'success');
      } else {
        const newRecord = await OccupancyService.createOccupancy(editedItem.value);
        occupancies.value.unshift(newRecord);
        showSnackbar('Occupancy record created successfully!', 'success');
      }
      closeDialog();
    } catch (error) {
      console.error("Error saving occupancy record:", error.response?.data?.error || error.message);
      showSnackbar(`Error: ${error.response?.data?.error || 'Could not save record.'}`, 'error', 5000);
    } finally {
      dialogLoading.value = false;
    }
  };
  
  const confirmDelete = async () => {
    if (!itemToDelete.value?.id) return;
    dialogLoading.value = true;
    try {
      await OccupancyService.deleteOccupancy(itemToDelete.value.id);
      occupancies.value = occupancies.value.filter(o => o.id !== itemToDelete.value.id);
      showSnackbar('Occupancy record deleted successfully!', 'success');
      closeDeleteDialog();
    } catch (error)
     {
      console.error("Error deleting occupancy record:", error);
      showSnackbar(`Error: ${error.response?.data?.error || 'Could not delete record.'}`, 'error', 5000);
    } finally {
      dialogLoading.value = false;
    }
  };

  const openViewDialog = async (item) => {
    viewDialogLoading.value = true;
    viewDialog.value = true;
    viewItem.value = null; 
    try {
      const fullOccupancyData = await OccupancyService.getOccupancy(item.id);
      viewItem.value = fullOccupancyData;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to load occupancy details.';
      showSnackbar(errorMsg, 'error');
      console.error("Error fetching occupancy details for view:", error);
      viewDialog.value = false; 
    } finally {
      viewDialogLoading.value = false;
    }
  };

  const closeViewDialog = () => {
    viewDialog.value = false;
  };

  </script>
  
  <style scoped>
  .fill-height {
    min-height: calc(100vh - 64px); /* Adjusted for no footer on register page */
  }
  .primary--text { color: var(--v-theme-primary) !important; }
  .text-primary { color: var(--v-theme-primary) !important; }
  .v-card { border-top: 4px solid var(--v-theme-primary); }
  .v-toolbar-title { line-height: 1.5rem; }
  </style>